import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { CrawlStatusBadge } from '@/components/crawl-status-badge';
import { useCurrentIdentity } from '@/lib/identity-context';
import { formatDateTime, crawlDurationMs, formatDuration } from '@/lib/format';

type Crawl = components['schemas']['Crawl'];

interface CrawlHistoryTableProps {
  siteId: string;
}

export function CrawlHistoryTable({ siteId }: CrawlHistoryTableProps) {
  const { hasPermission } = useCurrentIdentity();
  const canApproveBaseline = hasPermission('baseline:approve');
  const queryClient = useQueryClient();

  const crawlsQuery = useQuery({
    queryKey: queryKeys.sites.crawls(siteId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/crawls', {
        params: { path: { siteId }, query: { page: 1, pageSize: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const approveBaseline = useMutation({
    mutationFn: async (crawlId: string) => {
      const { data, error } = await apiClient.POST('/sites/{siteId}/baseline', {
        params: { path: { siteId } },
        body: { crawlId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.baseline(siteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.drift(siteId) });
    },
  });

  const crawls: Crawl[] = crawlsQuery.data?.items ?? [];

  if (crawlsQuery.isLoading) return <p className="text-sm text-muted-foreground">Loading crawls…</p>;
  if (crawlsQuery.isError) return <p className="text-sm text-red-600">Failed to load crawl history.</p>;

  return (
    <Table>
      <THead>
        <TR>
          <TH>Started</TH>
          <TH>Status</TH>
          <TH>Pages crawled</TH>
          <TH>Errored</TH>
          <TH>Duration</TH>
          <TH></TH>
        </TR>
      </THead>
      <TBody>
        {crawls.length === 0 ? (
          <TR>
            <TD colSpan={6} className="text-center text-sm text-muted-foreground">
              No crawls yet. Trigger one from the site header.
            </TD>
          </TR>
        ) : (
          crawls.map((c) => (
            <TR key={c.id} data-testid={`crawl-row-${c.id}`}>
              <TD>{formatDateTime(c.startedAt)}</TD>
              <TD>
                <CrawlStatusBadge status={c.status} />
              </TD>
              <TD>{c.pagesCrawled}</TD>
              <TD>{c.pagesErrored}</TD>
              <TD>{formatDuration(crawlDurationMs(c))}</TD>
              <TD className="text-right">
                <div className="flex justify-end gap-1">
                  <Link
                    to="/crawls/$crawlId"
                    params={{ crawlId: c.id }}
                    className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium hover:bg-muted"
                  >
                    View results
                  </Link>
                  {canApproveBaseline && c.status === 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={approveBaseline.isPending}
                      onClick={() => approveBaseline.mutate(c.id)}
                      data-testid={`approve-baseline-${c.id}`}
                    >
                      Approve baseline
                    </Button>
                  )}
                </div>
              </TD>
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}
